import { isActionOf } from "typesafe-actions";

import { hideToast, showErrorToast, showSuccessToast, showToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import type { IToast } from "app/components/React/Toast/types";
import { translate } from "app/filters/i18nFilter";
import { STATIC_LIST_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import { type LeadsType, selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import { clearOpportunityListQueries } from "app/pages/sales-intelligence/pages/opportunity-list/query/clearOpportunityListQueries";
import {
    saveMultipleContactsRevealedThunk,
    showMultipleRevealResultToastThunk,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects-reveal";
import { selectLeadsSearchContactsSearchRequestDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { exportContactRevealService } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/services/exportContactRevealService";
import dateTimeService from "app/services/date-time/dateTimeService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkActionCommon, ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import trackingService from "../../contacts-search/services/tracking/trackingService";
import { exportedToExcelAsync, multipleContactsWereRevealedAsync } from "../../contacts-search/store/action-creators";
import type { ContactsExportRequestDto, TopNRevealRequestDto } from "../../contacts-search/types/dto";
import { exportToExcelToastThunk } from "../../table-actions-panel/effects";
import { exportContactsToExcelService } from "../../table-actions-panel/services/exportExcelService";
import { extractCrmAndObjectType } from "../helpers/crmList";
import type {
    AddOpportunitiesToListDto,
    ExcludeListsDuplicateOpportunitiesDto,
    ListRecommendationsRequestParams,
    MoveOpportunitiesToListDto,
    MoveToListResponseDto,
    OpportunityListParams,
    OpportunityListType,
} from "../types";
import { OpportunityListVariant } from "../types";

import * as actionCreators from "./action-creators";

export const createOpportunityListThunk =
    (name: string, domains: readonly string[] = []) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.createOpportunityListAsync.request());

        const country = deps.si.settingsHelper.getInitialCountry();

        try {
            const response = await deps.si.api.opportunities.createOpportunityList({
                name,
                domains,
                country,
            });

            return dispatch(
                actionCreators.createOpportunityListAsync.success({
                    ...response.opportunitiesList,
                }),
            );
        } catch (e) {
            return dispatch(actionCreators.createOpportunityListAsync.failure(e));
        }
    };

export const createOpportunityListFromSearchThunk =
    (name: string, domains: readonly string[] = []) =>
    async (dispatch: ThunkDispatchCommon): Promise<OpportunityListType | undefined> => {
        const resultAction = await dispatch(createOpportunityListThunk(name, domains));
        const isSuccessAction = isActionOf(actionCreators.createOpportunityListAsync.success, resultAction);

        const shouldShowSuccessToast = domains.length > 0 && isSuccessAction;

        if (shouldShowSuccessToast) {
            let toast: IToast;

            const toastTextKey =
                domains.length > 1
                    ? "si.pages.search_result.add_leads_to_list.success.plural"
                    : "si.pages.search_result.add_leads_to_list.success";

            const onClick = () => {
                proNavigator.go(STATIC_LIST_PAGE_ROUTE, {
                    id: resultAction.payload.opportunityListId,
                });
                dispatch(hideToast(toast));
            };

            ({ toast } = dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text: translate(toastTextKey),
                        linkText: translate("workspace.add_opportunities.success.see_list"),
                        onClick,
                    }),
                ),
            ));
        }

        return isSuccessAction ? resultAction.payload : undefined;
    };

/**
 * API request to update the `name` of the list by given `listId`
 * @param name
 * @param listId
 * @param listCountry - currently a mandatory parameter
 */
export function updateOpportunityListNameThunk(
    name: string,
    listId: OpportunityListType["opportunityListId"],
    listCountry: OpportunityListType["country"],
) {
    return async function updateOpportunityListName(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(actionCreators.updateOpportunityListAsync.request());

        try {
            const list = await deps.si.api.opportunities.updateOpportunityList(listId, {
                country: listCountry,
                friendlyName: name,
            });

            return dispatch(actionCreators.updateOpportunityListAsync.success(list));
        } catch (e) {
            return dispatch(actionCreators.updateOpportunityListAsync.failure(e?.message));
        }
    };
}

export const updateListOpportunitiesThunk =
    (listId: OpportunityListType["opportunityListId"], dto: AddOpportunitiesToListDto) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.updateListOpportunitiesAsync.request());

        try {
            const response = await deps.si.api.opportunities.addOpportunitiesToList(listId, dto);

            dispatch(actionCreators.updateListOpportunitiesAsync.success(response));

            return response;
        } catch (e) {
            dispatch(actionCreators.updateListOpportunitiesAsync.failure(e));
        }
    };

const moveListOpportunitiesThunk = (
    listId: OpportunityListType["opportunityListId"],
    dto: MoveOpportunitiesToListDto,
    includeRegionals: boolean,
) => {
    return async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<MoveToListResponseDto | null> => {
        dispatch(actionCreators.updateListOpportunitiesAsync.request());

        try {
            const response = await deps.si.api.opportunities.moveOpportunitiesToList(listId, dto, includeRegionals);

            dispatch(
                actionCreators.updateListOpportunitiesAsync.success({
                    ...response,
                    opportunityListId: response.moveToOpportunityListId,
                }),
            );

            return response;
        } catch (e) {
            dispatch(actionCreators.updateListOpportunitiesAsync.failure(e));
            return null;
        }
    };
};

export const updateListOpportunitiesFromSearchThunk =
    (list: OpportunityListType, dto: AddOpportunitiesToListDto) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        const result = await dispatch(updateListOpportunitiesThunk(list.opportunityListId, dto));
        const { opportunities = [] } = result ?? {};
        // Copied from old code
        const toastTextKey =
            opportunities.length > 1
                ? "si.pages.search_result.add_leads_to_list.success.plural"
                : "si.pages.search_result.add_leads_to_list.success";

        if (result) {
            clearOpportunityListQueries(deps.si.queryClient, list.opportunityListId);
        }

        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text: translate(toastTextKey),
                    linkText: translate("workspace.add_opportunities.success.see_list"),
                    onClick: () => {
                        proNavigator.go(STATIC_LIST_PAGE_ROUTE, {
                            id: list.opportunityListId,
                        });
                    },
                }),
            ),
        );
    };

export const updateListOpportunitiesWithSuccessModalThunk =
    (list: OpportunityListType, dto: AddOpportunitiesToListDto) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        const updatedList = await dispatch(updateListOpportunitiesThunk(list.opportunityListId, dto));
        if (!updatedList) {
            return;
        }

        clearOpportunityListQueries(deps.si.queryClient, list.opportunityListId);
        dispatch(actionCreators.setOpportunityListIdToShowUpdatedListModal(list.opportunityListId));

        return updatedList;
    };

export const moveOpportunitiesListWithSuccessModalThunk =
    (list: OpportunityListType, dto: MoveOpportunitiesToListDto, includeRegionals: boolean) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        const updatedList = await dispatch(moveListOpportunitiesThunk(list.opportunityListId, dto, includeRegionals));

        if (!updatedList) {
            const toast = getToastItemComponent({
                text: `${translate("si.common.toast.error")}`,
            });

            dispatch(showErrorToast(toast));
            return;
        }

        clearOpportunityListQueries(deps.si.queryClient, list.opportunityListId);
        clearOpportunityListQueries(deps.si.queryClient, updatedList.moveToOpportunityListId);
        dispatch(actionCreators.setOpportunityListIdToShowUpdatedListModal(updatedList.moveToOpportunityListId));

        return updatedList;
    };

export const excludeDuplicateOpportunitiesThunk =
    (
        listId: ExcludeListsDuplicateOpportunitiesDto["OpportunityListId"],
        domainsToCheck: ExcludeListsDuplicateOpportunitiesDto["Domains"],
    ) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, { si: { api } }: ThunkDependencies) => {
        const { request, success, failure } = actionCreators.fetchExcludeListsDuplicatesAsync;

        dispatch(request());

        try {
            const excludeListsDuplicatesData = await api.opportunities.fetchExcludeListDuplicatesForStaticList({
                OpportunityListId: listId,
                Domains: domainsToCheck,
            });

            return dispatch(success(excludeListsDuplicatesData));
        } catch (e) {
            return dispatch(failure(e));
        }
    };

/**
 * API request to delete a list via given `listId`
 * @param listId
 */
export function deleteOpportunityListThunk(listId: OpportunityListType["opportunityListId"]) {
    return async function deleteOpportunityList(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(actionCreators.deleteOpportunityListAsync.request());

        try {
            await deps.si.api.opportunities.deleteOpportunityList(listId);

            return dispatch(actionCreators.deleteOpportunityListAsync.success(listId));
        } catch (e) {
            return dispatch(actionCreators.deleteOpportunityListAsync.failure(e));
        }
    };
}

export const fetchListRecommendationsThunk =
    (listId: OpportunityListType["opportunityListId"]) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.fetchListRecommendationsAsync.request());

        const params: ListRecommendationsRequestParams = {
            numberOfResults: 20,
            opportunityListId: listId,
            date: dateTimeService.formatWithMoment(deps.si.settingsHelper.getLastSnapshotDate(), "YYYY-MM"),
        };

        try {
            const recommendations = await deps.si.api.opportunities.fetchListRecommendations(params);

            dispatch(actionCreators.fetchListRecommendationsAsync.success(recommendations));
        } catch (e) {
            dispatch(actionCreators.fetchListRecommendationsAsync.failure(e));
        }
    };

export const dismissRecommendationThunk =
    (listId: OpportunityListType["opportunityListId"], domain: string) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(actionCreators.dismissRecommendationAsync.request(domain));

        try {
            await deps.si.api.opportunities.dismissRecommendation(listId, domain);

            dispatch(actionCreators.dismissRecommendationAsync.success(domain));
        } catch (e) {
            dispatch(actionCreators.dismissRecommendationAsync.failure(e));
        }
    };

function revealTopNContactsThunk(listParams: OpportunityListParams, n: number) {
    return async function revealTopNContacts(
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(multipleContactsWereRevealedAsync.request());

        const { createToast, getToastTitle, getErrorToastTitle, getToastComponent } = exportContactRevealService;
        const toast = createToast(getToastTitle(n));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        try {
            const state = getState();

            const searchParams = selectLeadsSearchContactsSearchRequestDto(state);
            const activeLeadsType = selectActiveLeadsType(state);

            const dto: TopNRevealRequestDto = {
                ...searchParams,
                top: n,
            };

            const response = await deps.si.api.opportunities.revealTopNContacts({
                leadsType: activeLeadsType,
                listParams,
                dto,
            });
            let revealedContactsCount = response.uniqueCount;

            if (response.requestId) {
                const statusResponseCount = await deps.si.api.contacts.revealStatus(response.requestId);
                revealedContactsCount += statusResponseCount;
            }

            const resultAction = await dispatch(
                saveMultipleContactsRevealedThunk(
                    response,
                    revealedContactsCount,
                    n,
                    state.impersonation.impersonateMode,
                ),
            );

            dispatch(hideToast(toast));

            return { resultAction, toast, response };
        } catch (e) {
            trackingService.trackContactsBulkRevealFailure(n, n);

            dispatch(hideToast(toast));
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            return {
                resultAction: dispatch(multipleContactsWereRevealedAsync.failure(e?.message)),
                toast,
                response: undefined,
            };
        }
    };
}

export function revealTopNContactsWithToastThunk(listParams: OpportunityListParams, n: number) {
    return async function revealTopNContactsWithToast(dispatch: ThunkDispatchCommon) {
        const { resultAction } = await dispatch(revealTopNContactsThunk(listParams, n));

        return dispatch(showMultipleRevealResultToastThunk(resultAction, n));
    };
}

export type ExcelExportResult = { success: boolean; exportedCount: number };

export function exportContactsToExcelThunk(
    listParams: OpportunityListParams,
    dto: ContactsExportRequestDto,
    leadsType: LeadsType,
    isSilent?: boolean,
): ThunkActionCommon<Promise<ExcelExportResult>> {
    return async function exportContactsToExcel(
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(exportedToExcelAsync.request());
        const { createToast, getExportingToastTitle, getSuccessToastTitle, getErrorToastTitle, getToastComponent } =
            exportContactsToExcelService;
        const requestedCount = dto.top || dto.includeContacts?.length || 0;
        const toast = createToast(getExportingToastTitle(requestedCount));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        try {
            if (listParams.variant === OpportunityListVariant.Static) {
                await deps.si.api.opportunities.staticList[leadsType].exportStaticListContactsToExcel(
                    listParams.id,
                    dto,
                );
            } else if (listParams.variant === OpportunityListVariant.Crm) {
                const { crm, objectType } = extractCrmAndObjectType(listParams.id);
                await deps.si.api.opportunities.crmList[leadsType].exportCrmListContactsToExcel(crm, objectType, dto);
            }
            dispatch(hideToast(toast));
            if (!isSilent) {
                dispatch(exportToExcelToastThunk(getSuccessToastTitle()));
            }

            dispatch(exportedToExcelAsync.success());
            // Excel export is all-or-nothing: success means all requested contacts exported
            return { success: true, exportedCount: requestedCount };
        } catch (e) {
            dispatch(hideToast(toast));
            // Always show error toast
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            dispatch(exportedToExcelAsync.failure(e?.message));
            return { success: false, exportedCount: 0 };
        }
    };
}
